import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import React from 'react'
import { Cell, Label, Pie, PieChart, ResponsiveContainer } from 'recharts'
import CustomLabel from './CustomLabel'
import type { PieChartData, PieChartLabelValueNamePair } from './pie-chart.types'
import { Box, useTheme } from '@mui/material'
import { ViewBox } from 'recharts/types/util/types'
import { Props } from 'recharts/types/component/Label'
import InactiveView from './InactiveView'

const InactiveLabelContent: React.FC<{ viewBox?: ViewBox }> = ({ viewBox }) => (
  <InactiveView cx={viewBox && 'cx' in viewBox ? viewBox.cx ?? 0 : 0} cy={viewBox && 'cy' in viewBox ? viewBox.cy ?? 0 : 0} />
)

/*
 * This component renders the pie chart content
 */
const PieChartContent: React.FC<{
  chartHeight: number
  chartWidth: number
  cells: React.ReactNode[]
  labelValueNamePair?: PieChartLabelValueNamePair
  data: PieChartData[]
  innerRadius: number
  outerRadius: number
  chartClassName: string
  icon?: React.ReactElement
  labelAction?: React.ReactElement
  textColor?: string
  isInactive?: boolean
}> = ({
  chartHeight,
  chartWidth,
  data,
  cells,
  labelValueNamePair,
  innerRadius,
  outerRadius,
  chartClassName,
  labelAction,
  icon,
  textColor,
  isInactive,
}) => {
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const isNoDataAvailable = cells.length === 0 || data.length === 0
  const isFullCircle = isNoDataAvailable || data.length === 1
  const endAngle = isFullCircle ? -270 : -268
  const startAngle = isFullCircle ? 90 : 88
  const startPieAtTop = { startAngle, endAngle }
  const getContentLabel = (props: Props, labelValueNamePair: PieChartLabelValueNamePair): React.ReactNode => (
    <LabelContent
      labelValueNamePair={labelValueNamePair}
      offset={props?.offset ?? 0}
      innerRadius={innerRadius * 2}
      viewBox={props?.viewBox}
      icon={icon}
      action={labelAction}
      textColor={textColor}
    />
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: chartHeight, width: chartWidth }}>
        <ResponsiveContainer>
          <PieChart>
            <Pie
              className={chartClassName}
              data={isNoDataAvailable ? [{ name: t('common.noDataShort'), value: 1 }] : data}
              cx={'50%'}
              cy={outerRadius}
              innerRadius={innerRadius}
              outerRadius={outerRadius}
              nameKey="name"
              dataKey="value"
              paddingAngle={isFullCircle ? 0 : 4}
              cornerRadius={6}
              minAngle={10}
              {...startPieAtTop}
            >
              {isInactive && (
                <>
                  <Cell fill={theme.palette.grey.grey06} stroke={theme.palette.grey.grey06} />
                  <Label position="insideTop" content={InactiveLabelContent} />
                </>
              )}
              {isNoDataAvailable && !isInactive ? <Cell fill={theme.palette.grey.grey06} stroke={theme.palette.grey.grey06} /> : cells}
              {labelValueNamePair && !isInactive && (
                <Label value={labelValueNamePair.value} position="center" content={props => getContentLabel(props, labelValueNamePair)} />
              )}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  )
}

export default PieChartContent

interface LabelProps {
  labelValueNamePair: PieChartLabelValueNamePair
  offset: number
  innerRadius: number
  viewBox?: ViewBox
  icon?: React.ReactElement
  action?: React.ReactElement
  textColor?: string
}

const LabelContent: React.FC<LabelProps> = ({ labelValueNamePair, offset, innerRadius, viewBox, action, icon, textColor }) => {
  const { t } = useCustomTranslation()
  const name = typeof labelValueNamePair.name === 'string' ? t(labelValueNamePair.name) : labelValueNamePair.name

  return (
    <CustomLabel
      name={name}
      value={labelValueNamePair.value.toString()}
      additionalUnit={labelValueNamePair.additionalUnit}
      showAsterix={labelValueNamePair.showAsterix}
      offset={offset}
      innerWidth={innerRadius}
      icon={icon}
      cx={viewBox && 'cx' in viewBox ? viewBox.cx ?? 0 : 0}
      cy={viewBox && 'cy' in viewBox ? viewBox.cy ?? 0 : 0}
      iconButton={action}
      textColor={textColor}
    />
  )
}
